import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  Bell,
  BookOpen,
  Boxes,
  Layers,
  Shapes,
  Building2,
  ChevronDown,
  Contrast,
  Cpu,
  LayoutDashboard,
  KeyRound,
  LogOut,
  Map as MapIcon,
  Network,
  PanelLeftClose,
  PanelLeftOpen,
  RadioTower,
  ScanSearch,
  Settings,
  Users,
  WifiOff,
  SlidersHorizontal,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet } from 'react-router';
import { canAny, fetchInfo, isClientUser, isSuperadmin, logout } from '../api/client';
import { ChangePassword } from './ChangePassword';
import { isMockMode } from '../data';
import { useSites } from '../data/hooks';
import { logoFor, ME_KEY, useBranding, useMe } from '../features/auth/session';
import { useConnection, usePresenceCounters } from '../realtime/aggregates';
import { MockBadge } from '../ui/components';
import { formatNumber } from '../ui/format';
import { usePrefs } from './prefs';
import { useSiteScope } from './scope';

/**
 * `perm`: the rail entry is shown only with that permission somewhere (the server enforces it anyway); `ops`: only
 * with operations.view too (operational / technical pages, not part of the authority observer's portal); `superadmin`:
 * the platform superadmin only.
 */
const NAV_MAIN = [
  { to: '/', key: 'dashboard', icon: LayoutDashboard, end: true },
  { to: '/map', key: 'map', icon: MapIcon },
  { to: '/devices', key: 'devices', icon: Cpu },
  { to: '/discovered', key: 'discovered', icon: ScanSearch, perm: 'device.provision' },
  { to: '/sites', key: 'sites', icon: Building2, perm: 'site.view', ops: true },
  { to: '/infrastructure', key: 'infrastructure', icon: Network, perm: 'operations.view' },
  { to: '/gateways', key: 'gateways', icon: RadioTower, perm: 'gateway.view' },
  { to: '/alarms', key: 'alarms', icon: Bell, perm: 'alarm.view' },
  { to: '/groups', key: 'groups', icon: Layers, perm: 'group.view' },
  { to: '/configuration', key: 'configuration', icon: SlidersHorizontal, perm: 'device.configure' },
  { to: '/assets', key: 'assets', icon: Shapes, perm: 'asset.view' },
  { to: '/reports', key: 'reports', icon: BarChart3, perm: 'report.view' },
  { to: '/inventory', key: 'inventory', icon: Boxes, perm: 'inventory.view' },
] as const;
const NAV_BOTTOM = [
  { to: '/manuals', key: 'manuals', icon: BookOpen, superadmin: true },
  { to: '/admin/users', key: 'users', icon: Users, perm: 'user.view' },
  { to: '/settings', key: 'settings', icon: Settings },
] as const;
// Phone tab bar: by key (not by index, which shifts when the rail gets entries), filtered by permission like the rail.
const TAB_KEYS = ['map', 'devices', 'alarms', 'reports', 'settings'];
const TABS = TAB_KEYS.map((k) => [...NAV_MAIN, ...NAV_BOTTOM].find((n) => n.key === k)!);

function useOutside(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && close();
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, close]);
  return ref;
}

/** A portal account (no operations.view) works on its own client only: its name, no selector. */
/** A client account's client next to the logo, in capitals, with its county: "ARBORE • SUCEAVA". */
export function SiteName() {
  const sites = useSites().data ?? [];
  const top = sites.find((s) => !sites.some((p) => p.id === s.parentId));
  if (!top) return <span className="site-name ellipsis" />;
  return (
    <span className="site-name ellipsis">
      {[top.name, top.county].filter(Boolean).join(' • ').toLocaleUpperCase('ro')}
    </span>
  );
}

function SiteSelector() {
  const { t } = useTranslation();
  const sites = useSites();
  const { siteId, setSite } = useSiteScope();
  const [open, setOpen] = useState(false);
  const ref = useOutside(open, () => setOpen(false));
  const roots = (sites.data ?? []).filter((s) => s.kind !== 'zone');
  const current = roots.find((s) => s.id === siteId);
  return (
    <div className="site-select" ref={ref}>
      <button
        type="button"
        className="site-select-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="dot" style={{ color: 'var(--color-online)' }} aria-hidden />
        <span className="ellipsis">{current?.name ?? t('common.allSites')}</span>
        <ChevronDown size={15} className="muted" />
      </button>
      {open && (
        <div className="menu" role="listbox">
          <button
            type="button"
            role="option"
            aria-selected={siteId === null}
            className="menu-item"
            onClick={() => (setSite(null), setOpen(false))}
          >
            {t('common.allSites')}
          </button>
          {roots.map((s) => (
            <button
              key={s.id}
              type="button"
              role="option"
              aria-selected={s.id === siteId}
              className="menu-item"
              style={{ paddingLeft: s.parentId ? 22 : 10 }}
              onClick={() => (setSite(s.id), setOpen(false))}
            >
              {s.name}
              <span className="muted num" style={{ marginLeft: 'auto' }}>
                {formatNumber(s.deviceCount)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ConnectionIndicator() {
  const { t } = useTranslation();
  const state = useConnection((s) => s.state);
  const tone =
    state === 'live' ? 'online' : state === 'resyncing' || state === 'connecting' ? 'unknown' : 'warning';
  return (
    <span className={`badge badge-${tone}`} title={t(`realtime.${state}`)}>
      <span className="dot" aria-hidden />
      <span className="hide-mobile">{t(`realtime.${state}`)}</span>
    </span>
  );
}

function UserMenu() {
  const { t } = useTranslation();
  const me = useMe().data;
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [changing, setChanging] = useState(false);
  const ref = useOutside(open, () => setOpen(false));
  const initials = (me?.display_name ?? '?')
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const signOut = async () => {
    await logout().catch(() => undefined);
    usePrefs.getState().setSignedIn(false);
    qc.setQueryData(ME_KEY, null);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' && q.queryKey[0] !== 'branding' });
  };
  return (
    <div className="user-menu" ref={ref}>
      <button
        type="button"
        className="avatar"
        onClick={() => setOpen((o) => !o)}
        aria-label={me?.display_name}
        aria-expanded={open}
      >
        {initials}
      </button>
      {open && (
        <div className="menu menu-right">
          <div className="menu-head">
            <span className="muted">{t('user.signedInAs')}</span>
            <strong>{me?.display_name}</strong>
            <span className="mono muted">{me?.username}</span>
          </div>
          <button
            type="button"
            className="menu-item"
            onClick={() => {
              setOpen(false);
              setChanging(true);
            }}
          >
            <KeyRound size={15} /> {t('account.password.title')}
          </button>
          <button type="button" className="menu-item" onClick={() => void signOut()}>
            <LogOut size={15} /> {t('user.signOut')}
          </button>
        </div>
      )}
      {changing && <ChangePassword onClose={() => setChanging(false)} />}
    </div>
  );
}

function StatusBar() {
  const { t } = useTranslation();
  const info = useQuery({ queryKey: ['info'], queryFn: fetchInfo, staleTime: Infinity, retry: 1 });
  const c = usePresenceCounters();
  return (
    <footer className="statusbar">
      <span>
        {t('presence.online')} {formatNumber(c.online)} · {t('presence.offline')} {formatNumber(c.offline)} ·{' '}
        {t('presence.unknown')} {formatNumber(c.unknown)}
      </span>
      <span style={{ flex: 1 }} />
      <span>
        {info.data
          ? `${t('realtime.backend')} ${info.data.version}`
          : info.isError
            ? t('realtime.backendDown')
            : '…'}
      </span>
      <span>UI {__APP_VERSION__}</span>
    </footer>
  );
}

function LiveLostBanner() {
  const { t } = useTranslation();
  const state = useConnection((s) => s.state);
  if (state !== 'reconnecting') return null;
  return (
    <div className="banner-offline" role="status">
      <WifiOff size={14} /> {t('realtime.reconnecting')}
    </div>
  );
}

function RailLink({
  to,
  label,
  icon: Icon,
  end,
}: {
  to: string;
  label: string;
  icon: typeof Bell;
  end?: boolean;
}) {
  return (
    <NavLink to={to} end={end} className="rail-item" aria-label={label}>
      <Icon size={20} strokeWidth={1.6} aria-hidden />
      <span className="rail-label">{label}</span>
    </NavLink>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  const theme = usePrefs((s) => s.theme);
  const setTheme = usePrefs((s) => s.setTheme);
  const branding = useBranding().data;
  const alarms = usePresenceCounters((s) => s.alarms);
  const mark = logoFor(branding, theme, 'mark');
  const me = useMe().data;
  const clientUser = isClientUser(me);
  // Pinned (per-user preference, default): the rail stays expanded with labels and the content is resized.
  // Collapsed: icons only; the hovered entry alone extends to the right with its label, over the content.
  const navPinned = usePrefs((s) => s.navPinned);
  const setNavPinned = usePrefs((s) => s.setNavPinned);
  const ops = canAny(me, 'operations.view');
  const allowed = (n: object) =>
    (!('perm' in n) || canAny(me, n.perm as string)) &&
    (!('ops' in n) || ops) &&
    (!('superadmin' in n) || isSuperadmin(me));
  return (
    <div className={`shell${navPinned ? ' rail-pinned' : ''}${ops ? '' : ' no-status'}`}>
      <header className="topbar">
        <NavLink to="/" className="topbar-brand" aria-label={branding?.product_name}>
          {mark ? <img src={mark} alt="" /> : <RadioTower size={20} />}
        </NavLink>
        {/* the site selector is for platform users; a client's own accounts (observer 2026-10-09, site
            administrator / operator 2026-10-10) see their client and county instead */}
        {ops && !clientUser ? <SiteSelector /> : <SiteName />}
        {isMockMode() && <MockBadge />}
        <span className="topbar-product hide-mobile">{branding?.product_name}</span>
        <span className="topbar-spacer" />
        <ConnectionIndicator />
        {canAny(me, 'alarm.view') && (
          <NavLink to="/alarms" className="icon-btn" aria-label={t('nav.alarms')} title={t('nav.alarms')}>
            <Bell size={18} strokeWidth={1.6} />
            {alarms > 0 && <span className="icon-badge">{alarms > 99 ? '99+' : alarms}</span>}
          </NavLink>
        )}
        <button
          type="button"
          className="icon-btn theme-toggle"
          onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
          aria-label={t('settings.theme')}
          title={t('settings.theme')}
        >
          <Contrast size={18} strokeWidth={1.6} />
        </button>
        <UserMenu />
      </header>
      <nav className="rail" aria-label="Main">
        {NAV_MAIN.filter(allowed).map((n) => (
          <RailLink
            key={n.to}
            to={n.to}
            label={t(n.key === 'sites' && clientUser ? 'nav.locations' : `nav.${n.key}`)}
            icon={n.icon}
            end={'end' in n}
          />
        ))}
        <span className="rail-spacer" />
        {NAV_BOTTOM.filter(allowed).map((n) => (
          <RailLink
            key={n.to}
            to={n.to}
            label={t(n.key === 'users' && clientUser ? 'nav.usersClient' : `nav.${n.key}`)}
            icon={n.icon}
          />
        ))}
        <button
          type="button"
          className="rail-item rail-pin"
          onClick={() => setNavPinned(!navPinned)}
          aria-pressed={navPinned}
          aria-label={t(navPinned ? 'nav.collapse' : 'nav.pin')}
        >
          {navPinned ? (
            <PanelLeftClose size={20} strokeWidth={1.6} aria-hidden />
          ) : (
            <PanelLeftOpen size={20} strokeWidth={1.6} aria-hidden />
          )}
          <span className="rail-label">{t(navPinned ? 'nav.collapse' : 'nav.pin')}</span>
        </button>
      </nav>
      <main className="main">
        <LiveLostBanner />
        <Outlet />
      </main>
      {ops && <StatusBar />}
      <nav className="tabbar" aria-label="Tabs">
        {TABS.filter(allowed).map((n) => (
          <NavLink key={n.to} to={n.to} className="tab">
            <n.icon size={20} strokeWidth={1.6} />
            {t(`nav.${n.key}`)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
